// A streamed Mac window's player: what the chrome (macwindow.js) reads and asks of its stream — MacWindowPlayer's
// state (the note and its symbol, the frame rate and idle trickle, the pointer style), the frames painted onto
// the window's canvas as they are decoded, the locked band, and dictation into the Mac's focused field (Web
// Speech, where the browser has it).
//
// `makePlayer(spec, link, w, { paintPicture, drawPointer, closeStream, openMacWindow, streamKeys, note })`; `drawLockBanner(spec)`
// paints the band over the last picture (app.js keeps it when a frame is still in flight).
import { Dictation, newWords, sessionText } from '../../core/dictation.js'

/// A band across the streamed picture while the Mac is locked: the lock, the words, and what to do.
export function drawLockBanner(spec) {
  const c = spec.canvas?.getContext('2d'); if (!c) return
  const W = spec.canvas.width, H = spec.canvas.height
  c.setTransform(1, 0, 0, 1, 0, 0)
  c.fillStyle = 'rgba(12,14,22,0.82)'
  c.fillRect(0, H * 0.4, W, H * 0.2)
  c.fillStyle = '#fff'; c.textAlign = 'center'; c.textBaseline = 'middle'
  c.font = `700 ${Math.round(H * 0.055)}px -apple-system, system-ui, sans-serif`
  c.fillText('🔒  The Mac is locked', W / 2, H * 0.47)
  c.fillStyle = 'rgba(255,255,255,0.75)'
  c.font = `500 ${Math.round(H * 0.03)}px -apple-system, system-ui, sans-serif`
  c.fillText('The picture is paused, and nothing you do here lands until it is unlocked at the Mac.', W / 2, H * 0.545)
  c.textAlign = 'left'
  if (spec.texture) spec.texture.needsUpdate = true
}

/// What the chrome reads and asks of its stream (MacWindowPlayer's state, windows/macwindow.js's ctx.stream).
export function makePlayer(spec, link, w, { paintPicture, drawPointer, closeStream, openMacWindow, streamKeys, note }) {
  const NOTES = {
    connecting: ['Connecting…', 'dot.radiowaves.left.and.right'],
    closed: ['That window was closed on the Mac.', 'xmark.circle'],
    locked: ['The Mac is locked — the picture is paused, and nothing you do lands until it is unlocked at the Mac', 'lock'],
    quiet: ['Not on screen on the Mac right now — minimized, hidden, or behind a full-screen app', 'moon.zzz'],
    covered: ['Covered by another window on the Mac — many apps stop drawing until they are in front', 'rectangle.stack'],
  }
  const flags = { closed: false, locked: false, quiet: false, framed: false, failure: null }
  let sentOptions = null   // the options the Mac has (JSON), so the same ones are never sent twice
  const player = {
    window: w, aspect: null, canControl: false, covered: false, idle: false,
    note: NOTES.connecting[0], noteSymbol: NOTES.connecting[1], reconnectIn: null,
    listening: false, transcript: '', speechProblem: null,
    prefs: null, lastTouch: Date.now(),
    rate() { const f = player.prefs?.maxFPS || 30; return player.idle ? Math.min(5, f) : f },
    options() { const p = player.prefs || {}; return { maxFPS: spec.attentive === false ? 1 : player.rate(), capturesPointer: p.pointerStyle === 'mac', staysBehind: !!p.staysBehind } },
    // opened once; options sent only when they differ from the last sent (a redraw asking again sends nothing —
    // every open restarts capture on the Mac)
    start(prefs) {
      player.prefs = prefs
      if (player.started) return
      player.started = true
      const o = player.options(); sentOptions = JSON.stringify(o)
      link.open(w.id, o.maxFPS, { capturesPointer: o.capturesPointer, staysBehind: o.staysBehind })
    },
    sendOptions() { const o = player.options(), k = JSON.stringify(o); if (k === sentOptions) return false; sentOptions = k; link.options(o); return true },
    apply(prefs) { player.prefs = prefs; spec.scrollMode = !!prefs.scrollMode; player.sendOptions(); drawPointer(spec) },
    send(ev) { link.send('input', ev) },
    // Hints (stream-hints.js): its messages out, and the Mac's back to whoever set `onHints`
    hintsSend(m) { link.send('hints', m) },
    onHints: null,
    touched() { player.lastTouch = Date.now(); if (player.idle) { player.idle = false; player.sendOptions(); link.keyframe(); rerender() } },
    reconnectNow() { closeStream(spec); openMacWindow(w) },
    toggleDictation() { dictate(spec) },
    get softKeyboard() { return matchMedia('(hover: none) and (pointer: coarse)').matches },
    get keyboardUp() { return streamKeys.softUp(spec) },
    toggleKeyboard() { streamKeys.toggleSoft(spec) },
    pasteHere() {
      if (!navigator.clipboard?.readText) { note('This browser has no clipboard to paste from.'); return }
      navigator.clipboard.readText().then((text) => { streamKeys.typeText(spec, text); streamKeys.take(spec) }, () => { note('The browser would not share its clipboard.') })
    },
    renote() {
      const key = flags.closed ? 'closed' : flags.locked ? 'locked' : flags.quiet ? 'quiet' : player.covered ? 'covered' : flags.framed ? null : 'connecting'
      if (flags.failure) { player.note = flags.failure; player.noteSymbol = 'exclamationmark.triangle' }
      else if (key) { player.note = NOTES[key][0]; player.noteSymbol = NOTES[key][1] }
      else { player.note = null }
      rerender()
    },
  }
  // the stream window's chrome redraws; a stream inside a pane tells its pane (app.js embedMacWindow)
  const rerender = () => { try { spec.chromeSpec?.dom.render(); spec.onPlayerChange?.() } catch (e) { console.error(e) } }
  link.onHints = (m) => player.onHints?.(m)
  link.onFormat = (format) => { player.canControl = format?.canControl !== false; rerender() }
  link.onFrame = (frame) => {
    window.cody && (window.cody.frames = (window.cody.frames || 0) + 1)
    const aspect = frame.displayWidth / Math.max(1, frame.displayHeight)
    if (!player.aspect || Math.abs(aspect - player.aspect) > 0.005) {
      // the Mac window's proportions: the canvas takes them, the chrome follows (and resizes the planes)
      player.aspect = aspect
      spec.canvas.width = Math.min(2400, frame.displayWidth); spec.canvas.height = Math.round(spec.canvas.width / aspect)
      spec.texture?.dispose()
      rerender()
    }
    if (!flags.framed || flags.failure) { flags.framed = true; flags.failure = null; player.renote() }
    paintPicture(spec, frame)
  }
  link.onFailure = (why) => { note(spec.short + ': ' + why); flags.failure = why; player.renote() }
  link.onState = (st) => {
    flags.closed = !!st.closed; flags.locked = st.locked === true; flags.quiet = !!st.quiet
    player.covered = st.covered === true
    spec.locked = flags.locked
    // the Mac pauses capture while locked, so no frame will come to carry the band: draw it over the last picture
    if (flags.locked && !spec.wasLocked) drawLockBanner(spec)
    if (st.locked === false && spec.wasLocked) link.keyframe()
    spec.wasLocked = flags.locked
    player.renote()
  }
  // idle: ninety seconds untouched (with Trickle on) drops the stream to 5 fps until the next touch
  spec.idleTimer = setInterval(() => {
    if (player.idle || !player.prefs?.idleDrop || Date.now() - player.lastTouch < 90000) return
    player.idle = true; player.sendOptions(); rerender()
  }, 15000)
  return player
}

/// Dictation into the focused field on the Mac (Web Speech, where the browser has it): what is heard goes as text,
/// each word once — the final results through the headset's rule (core/dictation.js), only the words not typed yet.
export function dictate(spec) {
  const p = spec.player
  if (p.listening) { p.recognizer?.stop(); return }
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition
  if (!SR) { p.speechProblem = 'Dictation is not available in this browser.'; spec.chromeSpec.dom.render(); setTimeout(() => { p.speechProblem = null; spec.chromeSpec?.dom.render() }, 4000); return }
  const r = new SR()
  r.continuous = true; r.interimResults = true
  p.recognizer = r; p.listening = true; p.transcript = ''; p.speechProblem = null
  const finals = new Dictation()
  let typed = ''
  r.onstart = () => finals.began()
  r.onresult = (e) => {
    const list = Array.from(e.results)
    const heard = finals.result(list.filter((x) => x.isFinal))
    const more = newWords(typed, heard)
    if (more) spec.stream.send('input', { action: 'text', text: more + ' ' })
    typed = heard
    p.transcript = sessionText(list.filter((x) => !x.isFinal))
    spec.chromeSpec?.dom.render()
  }
  r.onerror = (e) => { p.speechProblem = 'Dictation: ' + e.error }
  r.onend = () => { p.listening = false; p.transcript = ''; spec.chromeSpec?.dom.render() }
  try { r.start() } catch (e) { p.listening = false; p.speechProblem = e.message }
  spec.chromeSpec.dom.render()
}
